import { CustomTableComponent, CustomTableConfig } from 'commons/components';
import { PocBanner } from 'commons/components/components-wrapper/poc-banner.component';
import { PocButton } from 'commons/components/components-wrapper/poc-button.component';
import { CustomTableSortItems } from 'commons/components/table/sort/custom-table-sorting.model';
import { useTranslation } from 'core/i18n/i18n';
import { USER_LOCATION_ASSIGNMENT_I18N_PREFIX } from '../settings-user-location-assignment.constants';
import { TenantUserRow } from '../settings-user-location-assignment.vm';
import { TenantUsersBannerSlot } from './settings-user-location-assignment-list.style';

interface SettingsUserLocationAssignmentListComponentProps {
    users: TenantUserRow[];
    config: CustomTableConfig<TenantUserRow>;
    isLoading: boolean;
    isDegraded: boolean;
    hasFailedToLoad: boolean;
    defaultSort: CustomTableSortItems;
    onSortOrderChange: (sortItems: CustomTableSortItems) => void;
    onRetry: VoidFunction;
    onClickRow: (userId: string) => void;
    selectedRowIds: Set<string>;
    setSelectedRowIds: React.Dispatch<React.SetStateAction<Set<string>>>;
    onSelectRow: (userId: string, selected: boolean) => void;
    rowOptions: (user: TenantUserRow) => React.ReactElement[];
}

export const SettingsUserLocationAssignmentListComponent: React.FC<
    SettingsUserLocationAssignmentListComponentProps
> = ({
    users,
    config,
    isLoading,
    isDegraded,
    hasFailedToLoad,
    defaultSort,
    onSortOrderChange,
    onRetry,
    onClickRow,
    selectedRowIds,
    setSelectedRowIds,
    onSelectRow,
    rowOptions,
}) => {
    const { t } = useTranslation();

    // One failure banner, never two: hasFailedToLoad means there are no rows behind it, so it must
    // not be mistaken for the empty tenant the table would otherwise report.
    const failureKey = hasFailedToLoad ? 'unavailable' : 'degraded';

    // Empty on a hard failure: there are no rows because the read failed, not because the tenant is
    // empty, and the banner above already says so.
    const emptyMessage = hasFailedToLoad ? '' : t(`${USER_LOCATION_ASSIGNMENT_I18N_PREFIX}.table.empty`);

    return (
        <>
            {isDegraded || hasFailedToLoad ? (
                <TenantUsersBannerSlot>
                    <PocBanner status="warning" data-testid="tenant-users-degraded-banner">
                        {t(`${USER_LOCATION_ASSIGNMENT_I18N_PREFIX}.banner.${failureKey}`)}
                        <PocButton slot="action" variant="secondary" onClick={onRetry}>
                            {t(`${USER_LOCATION_ASSIGNMENT_I18N_PREFIX}.banner.retry`)}
                        </PocButton>
                    </PocBanner>
                </TenantUsersBannerSlot>
            ) : null}

            <CustomTableComponent<TenantUserRow>
                headerTitle={t(`${USER_LOCATION_ASSIGNMENT_I18N_PREFIX}.table.title`)}
                loading={isLoading}
                totalElements={users.length}
                tableData={users}
                config={config}
                showTotalElements
                sortingProps={{ defaultSort, onSortOrderChange }}
                onClickRow={onClickRow}
                horizontalScroll
                showSelectionColumn
                selectedRowIds={selectedRowIds}
                setSelectedRowIds={setSelectedRowIds}
                onSelectRow={(id, event) => onSelectRow(id, event.detail)}
                rowOptions={rowOptions}
                emptyMessage={emptyMessage}
            />
        </>
    );
};
